import {DataStatus} from 'platform/components';

import {forwardRef, useImperativeHandle, useState} from 'react';

import {isNotNil} from 'ramda';

import {useGetLabourCatalogGridFilterCatalogCategoryQuery} from '@dms/api/workshop';
import {ActiveFolder, DataGridTreeFolderHandle, FolderTree} from '@dms/shared-aftersales';

import {TestIdProps, suffixTestId} from 'shared/web';

const FOLDER_CONTEXT_TARGET = 'labour-catalog';

interface LabourFolderTreeProps extends TestIdProps {
  serviceCaseId?: string;
  directSaleId?: string;
  onActiveFolderChange?: (folder: ActiveFolder | undefined) => void;
}

export const LabourFolderTree = forwardRef<DataGridTreeFolderHandle, LabourFolderTreeProps>(
  (props, ref) => {
    const [activeFolder, setActiveFolder] = useState<ActiveFolder | undefined>();
    const [includeSubfolders, setIncludeSubfolders] = useState<boolean>(true);

    useImperativeHandle(ref, () => ({activeFolderId: activeFolder?.id, includeSubfolders}), [
      activeFolder?.id,
      includeSubfolders,
    ]);

    const {
      data: labourCatalogList,
      isLoading: isLabourCatalogListLoading,
      isError: isLabourCatalogListError,
    } = useGetLabourCatalogGridFilterCatalogCategoryQuery({
      serviceCaseId: props.serviceCaseId,
      directSaleId: props.directSaleId,
    });

    const catalogueIds = labourCatalogList?.map((catalogue) => catalogue.value) ?? [];

    const handleFolderChange = (id?: string, contextId?: string) => {
      const nextFolder = isNotNil(id) && isNotNil(contextId) ? {id, contextId} : undefined;
      setActiveFolder(nextFolder);
      props.onActiveFolderChange?.(nextFolder);
    };

    return (
      <DataStatus isLoading={isLabourCatalogListLoading} isError={isLabourCatalogListError}>
        <FolderTree
          contextIds={catalogueIds}
          contextTarget={FOLDER_CONTEXT_TARGET}
          onFolderChange={handleFolderChange}
          activeFolderId={activeFolder?.id}
          shouldIncludeSubfolders={includeSubfolders}
          onIncludeSubfoldersChange={setIncludeSubfolders}
          data-testid={suffixTestId('catalogueTree', props)}
        />
      </DataStatus>
    );
  }
);
